<template>
  <div class="billing-report">
    <div class="columns">
      <div class="column col-5 col-md-12 col-sm-12 form-group"
           :class="{'has-error': periodError}">
        <label class="form-label">{{ periodLabel }}</label>
        <st-date-period
          :start-date.sync="filters.startDate"
          :end-date.sync="filters.endDate"
          :periods="periods"
          :max-months="12"
          :start-date-validator="$v.filters.startDate"
          :end-date-validator="$v.filters.endDate"
          @blur:startDate="$v.filters.startDate.$touch()"
          @blur:endDate="$v.filters.endDate.$touch()"
        />
      </div>
      <div class="column col-md-12 col-sm-12 form-group"
           :class="hasExtraFilter ? 'col-4' : 'col-7'">
        <label class="form-label" :for="`${reportKey}-insurance`">Convênio</label>
        <select class="form-select" :id="`${reportKey}-insurance`" v-model="filters.planId">
          <option value="">[Todos os convênios]</option>
          <option v-for="item in insurances" :value="item.id" :key="item.id">
            {{ item.name }}
          </option>
        </select>
      </div>
      <div class="column col-3 col-md-12 col-sm-12 form-group" v-if="showProfessional">
        <label class="form-label" :for="`${reportKey}-professional`">Profissional</label>
        <select class="form-select" :id="`${reportKey}-professional`"
                v-model="filters.professionalId">
          <option value="">[Todos os profissionais]</option>
          <option v-for="item in professionals" :value="item.id" :key="item.id">
            {{ item.name }}
          </option>
        </select>
      </div>
      <div class="column col-3 col-md-12 col-sm-12 form-group" v-if="showStatus">
        <label class="form-label" :for="`${reportKey}-status`">Situação do lote</label>
        <select class="form-select" :id="`${reportKey}-status`" v-model="filters.status">
          <option value="">[Todas as situações]</option>
          <option v-for="item in statusItems" :value="item.value" :key="item.value">
            {{ item.text }}
          </option>
        </select>
      </div>
    </div>

    <div class="gr-legend" v-if="columns.length > 0">
      <span class="gr-legend-title">O relatório traz</span>
      <span class="gr-legend-item" v-for="column in columns" :key="column">{{ column }}</span>
    </div>

    <div class="footer">
      <button class="btn btn-gray btn-icon-left mr-1"
              :class="{loading: running === 'excel'}"
              :disabled="!!running"
              @click="run('excel')">
        <fa-icon :icon="['fal', 'file-excel']"></fa-icon>Gerar planilha
      </button>
      <button class="btn btn-primary btn-icon-left"
              :class="{loading: running === 'pdf'}"
              :disabled="!!running"
              @click="run('pdf')">
        <fa-icon :icon="['fal', 'print']"></fa-icon>Imprimir
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { date, minDate, maxDate } from '@/data/validators';
import { statuses as batchStatuses } from '@/data/billing-batch-statuses';
import * as professionalApi from '@/app/professional/api';

export default {
  props: {
    reportKey: {
      type: String,
      required: true,
    },
    route: {
      type: String,
      required: true,
    },
    fileName: {
      type: String,
      required: true,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    periodLabel: {
      type: String,
      default: 'Período da glosa',
    },
    showProfessional: {
      type: Boolean,
      default: false,
    },
    showStatus: {
      type: Boolean,
      default: false,
    },
    statusOptions: {
      type: Array,
      default: () => Object.keys(batchStatuses),
    },
    defaultDays: {
      type: Number,
      default: 90,
    },
  },
  data() {
    return {
      running: null,
      insurances: [],
      professionals: [],
      periods: [
        { value: 'd:-30', text: 'Últimos 30 dias' },
        { value: 'd:-60', text: 'Últimos 60 dias' },
        { value: 'd:-90', text: 'Últimos 90 dias' },
        { value: 'd:-180', text: 'Últimos 180 dias' },
        { value: 'd:-360', text: 'Últimos 12 meses' },
        { value: 'month', text: 'Este mês' },
        { value: 'month:-1', text: 'Mês passado' },
      ],
      filters: {
        startDate: moment().subtract(this.defaultDays, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        planId: '',
        professionalId: '',
        status: '',
      },
    };
  },
  validations() {
    const { startDate } = this.filters;
    const validStart = startDate && moment(startDate, 'YYYY-MM-DD', true).isValid();
    return {
      filters: {
        startDate: { date, required },
        endDate: {
          date,
          required,
          minDate: minDate(validStart ? startDate : null),
          maxDate: maxDate(validStart ? moment(startDate).add(12, 'months') : null),
        },
      },
    };
  },
  computed: {
    periodError() {
      return this.$v.filters.startDate.$error || this.$v.filters.endDate.$error;
    },
    hasExtraFilter() {
      return this.showProfessional || this.showStatus;
    },
    statusItems() {
      return this.statusOptions
        .filter(value => !!batchStatuses[value])
        .map(value => ({ value, text: batchStatuses[value] }));
    },
  },
  mounted() {
    this.loadInsurances();
    if (this.showProfessional) {
      this.loadProfessionals();
    }
  },
  methods: {
    loadInsurances() {
      return this.$http.get('/insurance-plans', { params: { limit: 0, active: true } })
        .then(({ data }) => {
          this.insurances = data.items
            .filter(item => item.type !== 'particular')
            .map(item => ({ id: item.plan.id, name: item.customName }));
        })
        .catch(() => {});
    },
    loadProfessionals() {
      return professionalApi.allActive()
        .then((data) => {
          this.professionals = (data.items || []).map(item => ({ id: item.id, name: item.name }));
        })
        .catch(() => {});
    },
    params(format) {
      const params = {
        format,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
      };
      if (this.filters.planId) {
        params.planId = this.filters.planId;
      }
      if (this.showProfessional && this.filters.professionalId) {
        params.professionalId = this.filters.professionalId;
      }
      if (this.showStatus && this.filters.status) {
        params.status = this.filters.status;
      }
      return params;
    },
    run(format) {
      this.$v.filters.$touch();
      if (this.$v.filters.$error) return;

      this.running = format;
      const params = this.params(format);
      const request = format === 'excel'
        ? this.$file.download(this.route, params, { name: this.fileName })
        : this.$file.print(this.route, params);

      Promise.resolve(request)
        .catch(() => {})
        .finally(() => {
          this.running = null;
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables";

.billing-report {
  .gr-legend {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
    margin-bottom: $layout-spacing;
  }
  .gr-legend-title {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
  }
  .gr-legend-item {
    background-color: $gray-color-ultra-light;
    border-radius: $border-radius;
    color: $body-font-color;
    font-size: $font-size-xs;
    padding: 0 .3rem;
  }
  .footer {
    border-top: $border-color solid $border-width;
    margin-top: $layout-spacing-lg;
    padding-top: $layout-spacing;
    text-align: right;
  }
}
</style>
